import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/axios';
import { useAuth } from '../context/AuthContext';
import styles from './Auth.module.css';

export const FirstTimeSetup: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setFirstTimeSetupRequired, getHomeForRole, user } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please provide an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.post('/api/user/setup-emails', {
        email: email,
      });
      
      setUser(res.data.user);
      setFirstTimeSetupRequired(false);
      
      const homePath = getHomeForRole(user?.role);
      navigate(homePath, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save email address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip? Without a recovery email, you will need to contact the admin to reset your password.')) {
      setFirstTimeSetupRequired(false);
      const homePath = getHomeForRole(user?.role);
      navigate(homePath, { replace: true });
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={`${styles.card} ${styles.cardWide}`}>
        <div className={styles.logoSection}>
          <div className={styles.logoIcon}>🎉</div>
          <h1 className={styles.logoTitle}>Welcome!</h1>
          <p className={styles.logoSubtitle}>Let's set up your account</p>
        </div>

        <div className={styles.infoBox}>
          <h3 className={styles.infoBoxTitle}>📧 Email Setup</h3>
          <p className={styles.infoBoxText}>Provide your email address to enable:</p>
          <ul className={styles.infoBoxList}>
            <li>📬 <strong>School announcements</strong> and important updates</li>
            <li>🔐 <strong>Password recovery</strong> - reset your password without contacting admin</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>📧 Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              className={styles.formInput}
            />
            <span className={styles.formHint}>This email will be used for announcements and password recovery</span>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              ⚠️ {error}
            </div>
          )}

          <div className={styles.buttonGroup}>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.primaryButton}
            >
              {isSubmitting ? 'Saving...' : 'Save & Continue'}
            </button>
            <button
              type="button"
              onClick={handleSkip}
              disabled={isSubmitting}
              className={styles.secondaryButton}
            >
              Skip
            </button>
          </div>
        </form>

        <p className={styles.footerText}>
          You can update your email address later in your profile settings
        </p>
      </div>
    </div>
  );
};
